import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	Dimmer,
	Form,
	Grid,
	Header,
	Icon,
	Input,
	List,
	Loader,
	Modal,
	ModalContent
} from 'semantic-ui-react';

import useForm from '../../../../../../hooks/useForm';

import { getParticipantsList, getProgramSubmissionTemplatesByType } from '../../../../../../api/nora/programs';
import { getSubmissionHistory, createSubmission, getSubmissionTypes } from '../../../../../../api/nora/submissions';

import { useProgramState } from '../../../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import TemplateTypeLabel from '../TemplateDetails/TemplateTypeLabel';
import FormSelect from '../../../../../Form/FormSelect';

import styles from '../../programs.module.css';
import FormInput from '../../../../../Form/FormInput';

const NewSubmissionModal = ( { open, onClose } ) => {
	const { program } = useProgramState();

	const { setApiErrorAlert } = useUIAction();

	const {
		formData,
		setFormData,
		updateField
	} = useForm( {
		template: 0,
		participants: [],
		instanceID: 0,
		label: ''
	} );

	const [ isLoading, setIsLoading ] = useState( false );
	const [ step, setStep ] = useState( 1 );
	const [ submissionTypes, setSubmissionTypes ] = useState( [] );
	const [ type, setType ] = useState();
	const [ templateOptions, setTemplateOptions ] = useState( [] );
	const [ submissionOptions, setSubmissionOptions ] = useState( [] );
	const [ available, setAvailable ] = useState( [] );
	const [ selected, setSelected ] = useState( [] );
	const [ search, setSearch ] = useState( '' );
	const [ agencies, setAgencies ] = useState( [] );

	useEffect( () => {
		( async () => {
			const [ { types } ] = await getSubmissionTypes( program.id );

			const mappedTypes = types.map( ( { id, name, description, value } ) => ( {
				key: id,
				description,
				name,
				value,
				id
			} ) );

			setSubmissionTypes( mappedTypes );
		} )();
	}, [ program.id ] );

	// step 1 - types
	const mapSubmissionTypes = useCallback( () => submissionTypes.map( ( {
		key,
		description,
		name,
		value,
		id
	} ) => {
		const nextStep = () => {
			setType( id );
			setStep( prev => prev + 1 );
		};

		return (
			<div
				key={ key }
				onClick={ nextStep }
				style= {{
					opacity: !value ? 0.4 : 1,
					pointerEvents: !value ? 'none' : 'initial',
					cursor: 'pointer',
					marginTop: '1rem'
				}}
			>
				<TemplateTypeLabel
					description={ description }
					text={ name }
				/>
			</div>
		);
	} ), [ submissionTypes ] );

	// step 2 - template & instance/label
	useEffect( () => {
		if ( type ) {
			( async () => {
				setIsLoading( true );

				try {
					const templates = await getProgramSubmissionTemplatesByType( program.id, type );

					const mappedTypes = templates.map( ( { id, name } ) => ( {
						key: id,
						text: name,
						value: id
					} ) );

					setTemplateOptions( mappedTypes );
				} catch ( error ) {
					setApiErrorAlert( error );
				}

				setIsLoading( false );
			} )();
		}
	}, [
		program.id,
		setTemplateOptions,
		setApiErrorAlert,
		type
	] );

	useEffect( () => {
		if ( formData.template && type === 101 ) {
			( async () => {
				setIsLoading( true );

				try {
					const { types } = await getSubmissionHistory( formData.template );

					const mappedTypes = types.map( ( { id, name } ) => ( {
						key: id,
						text: name,
						value: id
					} ) );

					setSubmissionOptions( mappedTypes );
				} catch ( error ) {
					setApiErrorAlert( error );
				}

				setIsLoading( false );
			} )();
		}
	}, [
		formData.template,
		setApiErrorAlert,
		setSubmissionOptions,
		type
	] );

	// step 3 - participants
	useEffect( () => {
		( async () => {
			setIsLoading( true );

			try {
				const data = await getParticipantsList( program.id );

				setAgencies( data );
			} catch ( error ) {
				setApiErrorAlert( error );
			}

			setIsLoading( false );
		} )();
	}, [
		program.id,
		setApiErrorAlert
	] );

	useEffect( () => {
		const availableAgencies = agencies
			.filter( a => !selected.includes( a.id )
				&& ( search.length
					? a.name.toLowerCase().includes( search.toLowerCase() )
					: true
				) );

		setAvailable( availableAgencies );
	}, [
		agencies,
		search,
		selected
	] );

	const mapAvailable = useCallback( () => available.length
		? available.map( ( { id, name } ) => {
			const handleClick = () => {
				setSelected( prev => [
					...prev,
					id
				] );
			};

			return <List.Item
				className={ styles.available }
				key={ id }
				onClick={ handleClick }
			>
				{ name }
			</List.Item>;
		} )
		: <List.Item className={ styles.available }>No results found.</List.Item>, [ available ] );

	const mapSelected = useCallback( () => selected.map( id => {
		const agency = agencies.find( a => a.id === id );

		const unselect = () => {
			setSelected( prev => prev.filter( p => p !== id ) );
		};

		return <List.Item
			className={ styles.selected }
			key={ agency.id }
		>
			{ agency.name }
			&nbsp;&nbsp;
			<Icon
				className={ styles.unselectParticipant }
				link
				name="times"
				onClick={ unselect }
			/>
		</List.Item>;
	} ), [
		agencies,
		selected
	] );

	const handleSearch = useCallback( ( e, { value } ) => {
		setSearch( value );
	}, [] );

	useEffect( () => {
		updateField( { target: { name: 'participants', value: selected } } );
	}, [
		selected,
		updateField
	] );

	// step 4 - confirm
	const typeLabel = useMemo( () => {
		if ( type ) {
			const { name, description } = submissionTypes.find( ( { id } ) => id === type );

			return (
				<TemplateTypeLabel
					description={ description }
					text={ name }
				/>
			);
		}

		return null;
	}, [ submissionTypes, type ] );

	const TemplateAndLabel = useMemo( () => {
		if ( formData.template ) {
			const { text } = templateOptions.find( ( { value } ) => value === formData.template );
			const instance = submissionOptions.find( ( { value } ) => value === formData.instanceID );

			return (
				<>
					<p style={ { marginTop: '1rem' } }><strong>Document:</strong> { text }</p>
					{
						formData.instanceID && instance
							? <p><strong>Label</strong>: { instance.text }</p>
							: <p><strong>Label</strong>: { formData.label }</p>
					}
				</>
			);
		}

		return null;
	}, [
		templateOptions,
		submissionOptions,
		formData.template,
		formData.instanceID,
		formData.label
	] );

	const mapParticipants = useCallback( () => formData.participants?.map( id => {
		const agency = agencies.find( a => a.id === id );

		return <List.Item
			key={ agency.id }
		>
			{ agency.name }
		</List.Item>;
	} ), [ agencies, formData.participants ] );

	// modal
	const handleClose = useCallback( () => {
		setFormData( [] );
		setStep( 1 );
		setType( null );
		setTemplateOptions( [] );
		setSubmissionOptions( [] );
		setAvailable( [] );
		setSelected( [] );
		setSearch( '' );

		onClose();
	}, [ onClose, setFormData ] );

	const handlePrev = useCallback( () => {
		if ( step === 2 ) {
			setType( null );
			setTemplateOptions( [] );
			setSubmissionOptions( [] );
			setFormData( {
				template: 0,
				participants: [],
				instanceID: 0,
				label: ''
			} );
			setStep( 1 );
		} else if ( step === 3 ) {
			setSearch( '' );
			setStep( 2 );
		} else if ( step === 4 ) {
			setStep( 3 );
		}
	}, [ setFormData, step ] );

	const handleNext = useCallback( () => {
		setStep( prev => prev + 1 );
	}, [] );

	const handleConfirm = useCallback( async () => {
		setIsLoading( true );

		try {
			const { template, ...data } = formData;

			await createSubmission( template, data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setIsLoading( false );
		handleClose();
	}, [
		formData,
		handleClose,
		setApiErrorAlert
	] );

	const handleTemplateChange = useCallback( target => {
		updateField( {
			target: {
				name: 'instanceID',
				value: 0
			}
		} );

		const label = submissionOptions.find( ( { id } ) => id === target.value )?.text;

		console.log( label );

		updateField( {
			target: {
				name: 'label',
				value: label
			}
		} );

		updateField( target );
	}, [ submissionOptions, updateField ] );

	return (
		<Modal
			className={ styles.newSubmissionModal }
			closeOnDimmerClick={ false }
			onClose={ handleClose }
			open={ open }
		>
			<Dimmer
				active={ isLoading }
				inverted
				style={{ borderRadius: 0 }}
			>
				<Loader inverted />
			</Dimmer>
			<ModalContent style={ { marginBottom: 'auto' } }>
				{ step === 1
					? <>
						<Header>Create New Submission</Header>
						<p>Please select the type of submission you would like to activate</p>
						{ mapSubmissionTypes() }
					</>
					: step === 2
						? <>
							<Header>Create New Submission</Header>
							<p>Please select the template you would like to deploy</p>
							<Form>
								<FormSelect
									fieldLabel="Template"
									name="template"
									onChange={ updateField }
									options={ templateOptions }
									value={ formData.template }
									width={ 8 }
								/>
								{
									type === 101 // program participant
										? <FormSelect
											disabled={ !formData.template }
											fieldLabel="Which instance of this template would you like to create?"
											name="instanceID"
											onChange={ handleTemplateChange }
											options={ submissionOptions }
											value={ formData.instanceID }
											width={ 8 }
										/>
										: <FormInput
											fieldLabel="What would you like to call this submission?"
											name="label"
											onChange={ updateField }
											value={ formData.label || '' }
											width={ 8 }
										/>
								}
							</Form>
						</>
						: step === 3
							? <Grid stretched>
								<Grid.Row>
									<Grid.Column width={ 8 }>
										<h5 className={ styles.columnHeader }>Available</h5>
										{ available.length || search
											? <>
												<Input
													className={ styles.availableSearch }
													fluid
													onChange={ handleSearch }
													icon="search"
													size="small"
													value={ search }
												/>
												<List className={ styles.availableList }>
													{ mapAvailable() }
												</List>
											</>
											: <p className={ styles.listEmptyMessage }>No available agencies.</p>
										}
									</Grid.Column>
									<Grid.Column width={ 8 }>
										<h5 className={ styles.columnHeader }>Selected</h5>
										{ selected.length
											? <List className={ styles.selectedList }>
												{ mapSelected() }
											</List>
											: <p className={ styles.listEmptyMessage }>Please select an agency.</p>
										}
									</Grid.Column>
								</Grid.Row>
							</Grid>
							: step === 4
								? <>
									{ typeLabel }
									{ TemplateAndLabel }
									<p><strong>Participants:</strong></p>
									<List>
										{ mapParticipants() }
									</List>
								</>
								: null
				}
			</ModalContent>
			<Modal.Actions>
				{ step > 1
					? <>
						<Button
							icon="arrow left"
							disabled={ step === 1 }
							onClick={ handlePrev }
						/>
						<Button
							icon="arrow right"
							disabled={ ( step === 2 && ( !formData.template || !( formData.label || formData.instanceID ) ) )
								|| ( step === 3 && !formData.participants?.length )
								|| step === 4
							}
							onClick={ handleNext }
						/>
					</>
					: null
				}
				<Button
					content="Cancel"
					onClick={ handleClose }
				/>
				{ step === 4
					? <Button
						color="pink"
						content="Confirm"
						onClick={ handleConfirm }
					/>
					: null
				}
			</Modal.Actions>
		</Modal>
	);
};

NewSubmissionModal.propTypes = {
	onClose: PropTypes.func,
	open: PropTypes.bool
};

export default NewSubmissionModal;
